/**
 * RioPromptEditsList — prompt-scoped Edits panel for the Rio drawer.
 *
 * Sibling of ``Templates/RioEditsList`` but scoped to the prompt-authoring
 * tool set (apply_edit_prompt_draft, apply_create_prompt, apply_start_test_run,
 * apply_edit_learner_profile_mock, apply_toggle_test_file,
 * apply_open_edit_prompt_pr, apply_open_new_prompt_pr).
 *
 * We don't share the template component because its ``describe`` switch
 * is hard-coded to template tool names and its ``ToolNameEnum`` doesn't
 * carry the prompt values yet. Rendering here reads ``msg.edit.tool_name``
 * as a plain string so a follow-up that adds prompt tools to the codegen
 * enum won't break this view.
 *
 * Tone styles + expand-for-diff UX match the template drawer so Rio's
 * Edits surface looks consistent across surfaces.
 */
import {
  ArrowRightIcon,
  ArrowTopRightOnSquareIcon,
  BeakerIcon,
  ChevronDownIcon,
  DocumentDuplicateIcon,
  DocumentTextIcon,
  PencilSquareIcon,
  PlusIcon,
  SparklesIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { type ComponentType, type FC, Fragment, type SVGProps, useState } from "react";

import { Small } from "@fronterahealth/frontera-ui-components";

import { type RioMessage, humanizeToolName } from "@pages/Templates/rioMessages";

interface RioPromptEditsListProps {
  edits: RioMessage[];
}

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

type EditTone = "ADD" | "UPDATE" | "TEST" | "PR" | "REMOVE" | "NEUTRAL";

interface Described {
  Icon: IconType;
  sentence: string;
  tone: EditTone;
}

const str = (v: unknown, fallback = ""): string => (typeof v === "string" && v.length > 0 ? v : fallback);

const bool = (v: unknown): boolean | null => (typeof v === "boolean" ? v : null);

const num = (v: unknown): number | null => (typeof v === "number" ? v : null);

/**
 * One-line human-readable summary for a prompt-scoped edit row. Prefer
 * args over pre_update_state for values (they name what the user asked
 * for); fall back to result_summary for tools we don't have a specific
 * case for so the row is never silently dropped.
 */
function describe(msg: RioMessage): Described | null {
  if (!msg.edit) return null;
  const { tool_name, args, result_summary } = msg.edit;
  const a = (args ?? {}) as Record<string, unknown>;

  // Cast to string for the switch — the orval-generated ToolNameEnum
  // only knows the template tool names right now. Prompt tools are
  // valid at runtime (RioToolName in api-django has them), but
  // regenerating types-and-hooks.ts requires re-running codegen
  // against a fresh schema.yaml. Doing the comparison as strings
  // keeps this component decoupled from the codegen cadence.
  const tool = tool_name as string;

  switch (tool) {
    case "apply_edit_prompt_draft": {
      const field = str(a.field, "field");
      return {
        Icon: PencilSquareIcon,
        sentence: `Updated draft ‘${field}’`,
        tone: "UPDATE",
      };
    }
    case "apply_construct_prompt_draft": {
      // The Prompt Constructor batches many field writes into one
      // tool call; args carry the intent + seed. The result_summary
      // lists which fields were applied. Show both so the row is
      // more informative than "used the constructor".
      const intent = str(a.intent);
      const shortIntent = intent.length > 80 ? intent.slice(0, 77) + "…" : intent;
      const summary = result_summary || "";
      // result_summary starts with "Constructed draft. Scope: X.\nApplied to
      // draft_overrides: field1, field2." — pull the applied list if present.
      const appliedMatch = summary.match(/Applied to draft_overrides: ([^.\n]+)/);
      const appliedFields = appliedMatch ? appliedMatch[1].trim() : "";
      const tail = appliedFields ? ` — fields: ${appliedFields}` : "";
      return {
        Icon: SparklesIcon,
        sentence: shortIntent
          ? `Constructor drafted from ‘${shortIntent}’${tail}`
          : `Constructor produced draft${tail}`,
        tone: "UPDATE",
      };
    }
    case "apply_create_prompt": {
      const key = str(a.key, "(new prompt)");
      const name = str(a.name);
      const label = name ? `‘${name}’ (${key})` : `‘${key}’`;
      return {
        Icon: PlusIcon,
        sentence: `Created prompt row ${label}`,
        tone: "ADD",
      };
    }
    case "apply_delete_prompt_draft": {
      const key = str(a.prompt_key, "(unknown)");
      return {
        Icon: TrashIcon,
        sentence: `Deleted draft prompt ‘${key}’`,
        tone: "REMOVE",
      };
    }
    case "apply_start_test_run": {
      const profileId = num(a.profile_id);
      const useDraft = bool(a.use_draft);
      const draftTail = useDraft === false ? " against the committed yaml" : "";
      return {
        Icon: BeakerIcon,
        sentence:
          profileId !== null
            ? `Started test run against profile #${profileId}${draftTail}`
            : `Started test run${draftTail}`,
        tone: "TEST",
      };
    }
    case "apply_edit_learner_profile_mock": {
      const profileId = num(a.profile_id);
      const updates = (a.updates ?? {}) as Record<string, unknown>;
      const count = Object.keys(updates).length;
      const target = profileId !== null ? `profile #${profileId}` : "profile";
      return {
        Icon: DocumentDuplicateIcon,
        sentence: `Updated ${target} mock (${count} key${count === 1 ? "" : "s"})`,
        tone: "UPDATE",
      };
    }
    case "apply_toggle_test_file": {
      const profileId = num(a.profile_id);
      const enabled = bool(a.enabled);
      const state = enabled === false ? "Disabled" : "Enabled";
      const target = profileId !== null ? ` on profile #${profileId}` : "";
      return {
        Icon: DocumentTextIcon,
        sentence: `${state} test file${target}`,
        tone: "UPDATE",
      };
    }
    case "apply_open_prompt_pr": {
      // result_summary: "ok: opened PR for [key1, key2] — pipelines: URL api-django: URL".
      const match = (result_summary ?? "").match(/opened PR for \[([^\]]+)\]/);
      const keys = match ? match[1].trim() : "";
      return {
        Icon: ArrowTopRightOnSquareIcon,
        sentence: keys ? `Opened PR for ${keys}` : "Opened prompt PR",
        tone: "PR",
      };
    }
    default:
      return {
        Icon: SparklesIcon,
        sentence: result_summary || humanizeToolName(tool_name),
        tone: "NEUTRAL",
      };
  }
}

const TONE_STYLES: Record<EditTone, { iconBg: string; iconText: string; leftBorder: string }> = {
  ADD: {
    iconBg: "bg-green-50",
    iconText: "text-green-600",
    leftBorder: "border-l-green-500",
  },
  UPDATE: {
    iconBg: "bg-amber-50",
    iconText: "text-amber-600",
    leftBorder: "border-l-amber-500",
  },
  TEST: {
    iconBg: "bg-blue-50",
    iconText: "text-blue-600",
    leftBorder: "border-l-blue-500",
  },
  PR: {
    iconBg: "bg-purple-50",
    iconText: "text-purple-600",
    leftBorder: "border-l-purple-500",
  },
  REMOVE: {
    iconBg: "bg-red-50",
    iconText: "text-red-600",
    leftBorder: "border-l-red-500",
  },
  NEUTRAL: {
    iconBg: "bg-bg-default",
    iconText: "text-text-secondary",
    leftBorder: "border-l-border-subtle",
  },
};

function formatDiffValue(v: unknown): string {
  if (v === null) return "null";
  if (v === undefined) return "(none)";
  if (typeof v === "string") return v;
  return JSON.stringify(v, null, 2);
}

const UpdateDiff: FC<{
  preUpdate: Record<string, unknown>;
  args: Record<string, unknown>;
}> = ({ preUpdate, args }) => {
  const entries = Object.entries(preUpdate);
  if (entries.length === 0) return null;
  return (
    <div className="mt-2 space-y-2 rounded bg-bg-default px-2 py-2">
      {entries.map(([key, oldVal]) => {
        // apply_edit_prompt_draft embeds the field name as the pre_update_state
        // key; the new value is at args.value.
        const newVal = key === "value" ? args.value : (args[key] ?? args.value);
        const isMultiline = typeof oldVal === "string" || typeof newVal === "string";
        return (
          <div key={key} className="text-xs">
            <div className="mb-0.5 font-medium text-text-secondary">{key}</div>
            <div className="space-y-1">
              <div className="rounded border border-red-200 bg-red-50 px-1.5 py-1 text-red-900">
                <span className="select-none text-red-400">− </span>
                <span className={isMultiline ? "whitespace-pre-wrap break-words" : "break-words"}>
                  {formatDiffValue(oldVal)}
                </span>
              </div>
              <div className="rounded border border-green-200 bg-green-50 px-1.5 py-1 text-green-900">
                <span className="select-none text-green-400">+ </span>
                <span className={isMultiline ? "whitespace-pre-wrap break-words" : "break-words"}>
                  {formatDiffValue(newVal)}
                </span>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

const EditRow: FC<{ msg: RioMessage }> = ({ msg }) => {
  const [expanded, setExpanded] = useState(false);
  const described = describe(msg);
  if (!described) return null;
  const { Icon, sentence, tone } = described;
  const styles = TONE_STYLES[tone];
  const hasUpdateDetails = !!msg.edit?.pre_update_state;
  const hasDeleteDetails = !!msg.edit?.pre_delete_state;
  const hasDetails = hasUpdateDetails || hasDeleteDetails;

  return (
    <li className={`rounded-md border border-border-subtle border-l-4 bg-bg-surface px-3 py-2 ${styles.leftBorder}`}>
      <button
        type="button"
        onClick={() => hasDetails && setExpanded((e) => !e)}
        className={`flex w-full items-center gap-2.5 text-left ${hasDetails ? "cursor-pointer" : "cursor-default"}`}
        disabled={!hasDetails}
        aria-expanded={hasDetails ? expanded : undefined}
      >
        <span
          className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${styles.iconBg}`}
          aria-hidden
        >
          <Icon className={`h-4 w-4 ${styles.iconText}`} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-sm text-text-primary">{sentence}</div>
        </div>
        {hasDetails && (
          <ChevronDownIcon
            className={`h-4 w-4 shrink-0 text-text-secondary transition-transform ${expanded ? "rotate-180" : ""}`}
          />
        )}
      </button>
      {expanded && hasUpdateDetails && (
        <UpdateDiff
          preUpdate={msg.edit?.pre_update_state as Record<string, unknown>}
          args={(msg.edit?.args ?? {}) as Record<string, unknown>}
        />
      )}
      {expanded && hasDeleteDetails && (
        <pre className="mt-2 max-h-64 overflow-auto rounded bg-bg-default px-2 py-1.5 text-xs text-text-secondary whitespace-pre-wrap break-words">
          {JSON.stringify(msg.edit?.pre_delete_state, null, 2)}
        </pre>
      )}
    </li>
  );
};

export const RioPromptEditsList: FC<RioPromptEditsListProps> = ({ edits }) => {
  if (edits.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center text-center">
        <Small className="text-text-secondary max-w-[280px]">
          Rio hasn&apos;t made any edits to this prompt yet. Ask Rio to update the draft, run a test, or open a PR —
          those actions will show up here.
        </Small>
      </div>
    );
  }
  const withArrow = edits.length > 1;
  return (
    <ul className="flex flex-col gap-2">
      {edits.map((m, idx) => (
        <Fragment key={m.message_id}>
          <EditRow msg={m} />
          {withArrow && idx < edits.length - 1 && (
            <li className="flex justify-center py-0.5" aria-hidden>
              <ArrowRightIcon className="h-3 w-3 rotate-90 text-border-subtle" />
            </li>
          )}
        </Fragment>
      ))}
    </ul>
  );
};
